/**
 * Markdown-subset renderer for archlang descriptions.
 *
 * Inputs are interpolated markdown produced by the core renderer:
 *   - `[[…]]` refs have already been resolved to `[name](archlang://…)` links
 *   - `@label` interpolations are already substituted with values (or
 *     replaced with `<missing:…>` sentinels)
 *
 * This renderer accepts only the markdown subset declared in
 * Specification §4.11.1: headers (all levels → single rank), emphasis
 * (bold/italic/strike/code), links, lists (ordered/unordered, nested),
 * tables (GFM pipe), blockquotes, fenced code blocks, horizontal rule.
 *
 * Output is HTML safe to inject via `dangerouslySetInnerHTML`. All
 * user-supplied text is HTML-escaped at the point of emission; only a
 * fixed allowlist of tags (`p`, `h*`, `strong`, `em`, `s`, `code`,
 * `pre`, `a`, `ul`, `ol`, `li`, `blockquote`, `hr`, `table`, `thead`,
 * `tbody`, `tr`, `th`, `td`, `span`, `img`) is ever emitted. Link `href`
 * and image `src` attributes are filtered through URL allowlists.
 *
 * The implementation is intentionally hand-rolled rather than bringing
 * in a markdown library — the subset is small, the parser is ~200
 * lines, and there is no untrusted-HTML input surface to worry about.
 */

const SAFE_URL_RE = /^(https?:\/\/|mailto:|archlang:\/\/|#|\.\.?\/|\/)/i;
/** Image sources: http(s), inline data:image, or a relative/absolute path. */
const SAFE_IMG_URL_RE = /^(https?:\/\/|data:image\/|\.\.?\/|\/)/i;

export function renderDescriptionHtml(markdown: string): string {
  if (!markdown) return "";
  const blocks = parseBlocks(normalizeNewlines(markdown));
  return blocks.map(renderBlock).join("\n");
}

/**
 * Parse an `archlang://` URI produced by the description renderer.
 * Returns one of:
 *   - `id/<id>`                 → `{ kind: "id", value }`
 *   - `name/<qualified>`        → `{ kind: "name", value }` (URI-decoded)
 *   - `doc/<name>#<slug>`       → `{ kind: "doc", doc, slug? }` (name URI-decoded,
 *                                  slug verbatim; `#` separates the two)
 *   - `aspect/<key>[:<value>]`  → `{ kind: "aspect", key, value? }` (both
 *                                  URI-decoded; `:` separates them)
 * Returns null for anything else.
 */
export type ArchlangUri =
  | { kind: "id"; value: string }
  | { kind: "name"; value: string }
  | { kind: "doc"; doc: string; slug?: string }
  | { kind: "aspect"; key: string; value?: string };

function decode(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function parseArchlangUri(href: string): ArchlangUri | null {
  if (!href.startsWith("archlang://")) return null;
  const rest = href.slice("archlang://".length);
  const slash = rest.indexOf("/");
  if (slash === -1) return null;
  const kind = rest.slice(0, slash);
  const raw = rest.slice(slash + 1);
  if (kind === "id" || kind === "name") {
    return { kind, value: decode(raw) };
  }
  if (kind === "doc") {
    // `doc/<encodeURIComponent(name)>#<slug>`. `#` is percent-encoded inside the
    // name, so the first raw `#` always separates name from section slug.
    const hash = raw.indexOf("#");
    if (hash === -1) return { kind: "doc", doc: decode(raw) };
    const slug = raw.slice(hash + 1);
    return { kind: "doc", doc: decode(raw.slice(0, hash)), slug: slug || undefined };
  }
  if (kind === "aspect") {
    // `aspect/<encodeURIComponent(key)>[:<encodeURIComponent(value)>]`. `:` is
    // percent-encoded inside each part, so the first raw `:` is the separator.
    const colon = raw.indexOf(":");
    if (colon === -1) return { kind: "aspect", key: decode(raw) };
    return {
      kind: "aspect",
      key: decode(raw.slice(0, colon)),
      value: decode(raw.slice(colon + 1)),
    };
  }
  return null;
}

/**
 * Delegated click handler for a `.arch-desc` container. Intercepts
 * left-clicks on `<a href="archlang://…">` links, calls `onNav`, and
 * prevents the browser from following the unhandled URI scheme.
 * Modifier-clicks (cmd/ctrl/shift/middle) fall through to default
 * browser behavior so power users still get e.g. "open in new tab"
 * semantics for external links — for archlang refs we always
 * intercept since the browser can't open them anyway.
 */
export function handleDescriptionClick(
  e: { target: EventTarget | null; preventDefault(): void; defaultPrevented: boolean },
  onNav: (href: string) => void
): void {
  const target = e.target as HTMLElement | null;
  if (!target || typeof target.closest !== "function") return;
  const anchor = target.closest("a");
  if (!anchor) return;
  const href = anchor.getAttribute("href");
  if (!href) return;
  if (!href.startsWith("archlang://")) return;
  e.preventDefault();
  onNav(href);
}

/**
 * Strip markdown formatting to plain text for one-line previews
 * (truncated list items, tooltips). Link syntax `[label](url)` reduces
 * to `label`; emphasis markers are dropped; `<missing:…>` sentinels
 * show as `?`.
 */
export function descriptionToPlainText(markdown: string): string {
  if (!markdown) return "";
  return markdown
    .replace(/<missing:[^>]+>/g, "?")
    .replace(/<unresolved:[^>]+>/g, "?")
    .replace(/<ambiguous:[^>]+>/g, "?")
    .replace(/`{1,3}([^`]+)`{1,3}/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^>\s?/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

// =============================================================================
// Block-level parsing
// =============================================================================

type Block =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "code"; lang: string; text: string }
  | { kind: "list"; ordered: boolean; items: Block[][] }
  | { kind: "blockquote"; children: Block[] }
  | { kind: "table"; header: string[]; aligns: string[]; rows: string[][] }
  | { kind: "hr" }
  | { kind: "missing"; key: string };

function normalizeNewlines(s: string): string {
  return s.replace(/\r\n?/g, "\n");
}

function parseBlocks(source: string): Block[] {
  const lines = source.split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    // Fenced code: ``` or ~~~
    const fence = /^(?:[ \t]*)(`{3,}|~{3,})\s*([^\s`~]*)\s*$/.exec(line);
    if (fence) {
      const marker = fence[1]!;
      const lang = fence[2] ?? "";
      i++;
      const start = i;
      while (i < lines.length && !lines[i]!.trim().startsWith(marker)) i++;
      blocks.push({ kind: "code", lang, text: lines.slice(start, i).join("\n") });
      if (i < lines.length) i++;
      continue;
    }
    // Horizontal rule.
    if (/^\s*(?:-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }
    // Heading.
    const heading = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", text: heading[2]! });
      i++;
      continue;
    }
    // Blockquote.
    if (/^\s{0,3}>/.test(line)) {
      const start = i;
      while (i < lines.length && /^\s{0,3}>/.test(lines[i]!)) i++;
      const inner = lines
        .slice(start, i)
        .map((l) => l.replace(/^\s{0,3}>\s?/, ""))
        .join("\n");
      blocks.push({ kind: "blockquote", children: parseBlocks(inner) });
      continue;
    }
    // Table.
    const tableEnd = peekTable(lines, i);
    if (tableEnd > i) {
      const headerCells = splitTableRow(lines[i]!);
      const aligns = splitTableRow(lines[i + 1]!).map(alignFor);
      const rows: string[][] = [];
      for (let r = i + 2; r < tableEnd; r++) {
        rows.push(splitTableRow(lines[r]!));
      }
      blocks.push({ kind: "table", header: headerCells, aligns, rows });
      i = tableEnd;
      continue;
    }
    // List.
    const listMatch = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
    if (listMatch) {
      const baseIndent = listMatch[1]!.length;
      const ordered = listMatch[3] !== undefined;
      const items: Block[][] = [];
      while (i < lines.length) {
        const m = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(lines[i]!);
        if (!m || m[1]!.length !== baseIndent) break;
        const itemOrdered = m[3] !== undefined;
        if (itemOrdered !== ordered) break;
        const itemLines: string[] = [m[4]!];
        i++;
        while (i < lines.length) {
          const next = lines[i]!;
          if (!next.trim()) {
            itemLines.push("");
            i++;
            continue;
          }
          if (/^(\s*)(?:[-*+]|\d+[.)])\s+/.test(next)) {
            const ni = next.match(/^(\s*)/)![1]!.length;
            if (ni <= baseIndent) break;
          }
          const indent = next.match(/^(\s*)/)![1]!.length;
          if (indent <= baseIndent && next.trim().length > 0) break;
          itemLines.push(next.replace(new RegExp(`^\\s{0,${baseIndent + 2}}`), ""));
          i++;
        }
        items.push(parseBlocks(itemLines.join("\n").trim()));
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }
    // Paragraph: collect until blank line, fence, or heading.
    const paraStart = i;
    while (i < lines.length) {
      const l = lines[i]!;
      if (!l.trim()) break;
      if (/^\s{0,3}(#{1,6}|>|(?:-{3,}|_{3,}|\*{3,})\s*$|(`{3,}|~{3,}))/.test(l)) break;
      if (/^(\s*)(?:[-*+]|\d+[.)])\s+/.test(l)) break;
      i++;
    }
    blocks.push({ kind: "paragraph", text: lines.slice(paraStart, i).join("\n") });
  }
  return blocks;
}

function peekTable(lines: readonly string[], i: number): number {
  if (i + 1 >= lines.length) return i;
  const header = lines[i]!;
  const sep = lines[i + 1]!;
  if (!/\|/.test(header)) return i;
  if (!/^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(sep)) return i;
  let end = i + 2;
  while (end < lines.length && /\|/.test(lines[end]!) && lines[end]!.trim()) end++;
  return end;
}

function splitTableRow(line: string): string[] {
  return line
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((c) => c.trim());
}

function alignFor(spec: string): string {
  const s = spec.trim();
  const left = s.startsWith(":");
  const right = s.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return "";
}

// =============================================================================
// Block rendering
// =============================================================================

function renderBlock(b: Block): string {
  switch (b.kind) {
    case "heading":
      return `<h4 class="arch-desc-h">${renderInline(b.text)}</h4>`;
    case "paragraph":
      return `<p>${renderParagraph(b.text)}</p>`;
    case "code":
      return `<pre><code${b.lang ? ` data-lang="${escapeAttr(b.lang)}"` : ""}>${escapeHtml(b.text)}</code></pre>`;
    case "hr":
      return `<hr/>`;
    case "blockquote":
      return `<blockquote>${b.children.map(renderBlock).join("\n")}</blockquote>`;
    case "list": {
      const tag = b.ordered ? "ol" : "ul";
      const items = b.items
        .map((children) => `<li>${children.map(renderBlock).join("\n")}</li>`)
        .join("\n");
      return `<${tag}>${items}</${tag}>`;
    }
    case "table": {
      const head = b.header
        .map((c, i) => {
          const align = b.aligns[i];
          const attr = align ? ` style="text-align:${align}"` : "";
          return `<th${attr}>${renderInline(c)}</th>`;
        })
        .join("");
      const body = b.rows
        .map((row) => {
          const cells = row
            .map((c, i) => {
              const align = b.aligns[i];
              const attr = align ? ` style="text-align:${align}"` : "";
              return `<td${attr}>${renderInline(c)}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");
      return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    }
    case "missing":
      return `<span class="arch-desc-missing">&lt;missing:${escapeHtml(b.key)}&gt;</span>`;
  }
}

// =============================================================================
// Inline rendering
// =============================================================================

/**
 * Render a paragraph honoring markdown line breaks: a line ending in 2+ spaces is a
 * **hard break** (`<br>`) — this is what a multi-line `~{ … }` calc napkin emits, so
 * its lines must not collapse into one. A bare newline is a soft break (a space).
 */
function renderParagraph(text: string): string {
  const lines = text.split("\n");
  return lines
    .map((line, i) => {
      const hard = / {2,}$/.test(line);
      const html = renderInline(line.replace(/ +$/, ""));
      if (i === lines.length - 1) return html;
      return html + (hard ? "<br/>" : " ");
    })
    .join("");
}

function renderInline(text: string): string {
  // Tokenize against backtick code-spans first so emphasis markers inside
  // code are untouched.
  const out: string[] = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i]!;
    if (ch === "`") {
      const start = i;
      while (text[i] === "`") i++;
      const ticks = i - start;
      const close = findInlineCodeClose(text, i, ticks);
      if (close !== -1) {
        out.push(`<code>${escapeHtml(text.slice(i, close))}</code>`);
        i = close + ticks;
        continue;
      }
      out.push(escapeHtml(text.slice(start, i)));
      continue;
    }
    // Image `![alt](src)` — optional `"title"` and `=WxH` size hint. Unsafe
    // srcs degrade to the alt text.
    if (ch === "!" && text[i + 1] === "[") {
      const link = matchLink(text, i + 1);
      if (link) {
        const parsed = parseImageHref(link.href);
        const safe = parsed && SAFE_IMG_URL_RE.test(parsed.url) ? parsed.url : null;
        if (safe !== null) {
          const dims =
            (parsed!.width ? ` width="${parsed!.width}"` : "") +
            (parsed!.height ? ` height="${parsed!.height}"` : "");
          out.push(`<img src="${escapeAttr(safe)}" alt="${escapeAttr(link.label)}"${dims}/>`);
        } else {
          out.push(renderInline(link.label));
        }
        i = link.end;
        continue;
      }
    }
    if (ch === "[") {
      const link = matchLink(text, i);
      if (link) {
        const { label, href, end } = link;
        const safe = safeHref(href);
        if (safe !== null) {
          out.push(`<a href="${escapeAttr(safe)}">${renderInline(label)}</a>`);
        } else {
          // Unsafe URL: drop the href entirely and render only the label
          // (already escaped via recursive renderInline).
          out.push(renderInline(label));
        }
        i = end;
        continue;
      }
    }
    if (
      ch === "<" &&
      text.startsWith("<missing:", i) &&
      text.indexOf(">", i) !== -1
    ) {
      const close = text.indexOf(">", i);
      const key = text.slice(i + "<missing:".length, close);
      out.push(
        `<span class="arch-desc-missing">&lt;missing:${escapeHtml(key)}&gt;</span>`
      );
      i = close + 1;
      continue;
    }
    if (
      ch === "<" &&
      (text.startsWith("<unresolved:", i) || text.startsWith("<ambiguous:", i)) &&
      text.indexOf(">", i) !== -1
    ) {
      const close = text.indexOf(">", i);
      const kind = text.startsWith("<unresolved:", i) ? "unresolved" : "ambiguous";
      const inner = text.slice(i + 1, close);
      out.push(
        `<span class="arch-desc-${kind}">&lt;${escapeHtml(inner)}&gt;</span>`
      );
      i = close + 1;
      continue;
    }
    // Emphasis: **bold**, *italic*, ~~strike~~
    const emph = matchEmphasis(text, i);
    if (emph) {
      out.push(`<${emph.tag}>${renderInline(emph.inner)}</${emph.tag}>`);
      i = emph.end;
      continue;
    }
    // Default: escape one char.
    out.push(escapeHtml(ch));
    i++;
  }
  return out.join("");
}

function findInlineCodeClose(text: string, from: number, ticks: number): number {
  let i = from;
  while (i < text.length) {
    if (text[i] === "`") {
      const start = i;
      while (text[i] === "`") i++;
      if (i - start === ticks) return start;
    } else {
      i++;
    }
  }
  return -1;
}

function matchLink(
  text: string,
  i: number
): { label: string; href: string; end: number } | null {
  if (text[i] !== "[") return null;
  let depth = 0;
  let j = i;
  while (j < text.length) {
    const c = text[j]!;
    if (c === "[") depth++;
    else if (c === "]") {
      depth--;
      if (depth === 0) break;
    }
    j++;
  }
  if (depth !== 0 || text[j + 1] !== "(") return null;
  const labelEnd = j;
  const hrefStart = j + 2;
  const hrefEnd = text.indexOf(")", hrefStart);
  if (hrefEnd === -1) return null;
  return {
    label: text.slice(i + 1, labelEnd),
    href: text.slice(hrefStart, hrefEnd).trim(),
    end: hrefEnd + 1,
  };
}

/** Split an image href into a url + optional `=WxH` size hint, dropping a
 *  trailing `"title"`. Returns null if no url. */
function parseImageHref(
  raw: string
): { url: string; width?: number; height?: number } | null {
  const m = /^(\S+)(?:\s+"[^"]*")?(?:\s+=(\d+)x(\d+))?\s*$/.exec(raw.trim());
  if (!m) return null;
  return {
    url: m[1]!,
    width: m[2] ? parseInt(m[2], 10) : undefined,
    height: m[3] ? parseInt(m[3], 10) : undefined,
  };
}

function matchEmphasis(
  text: string,
  i: number
): { tag: string; inner: string; end: number } | null {
  if (text.startsWith("**", i)) {
    const close = text.indexOf("**", i + 2);
    if (close !== -1)
      return { tag: "strong", inner: text.slice(i + 2, close), end: close + 2 };
  }
  if (text.startsWith("~~", i)) {
    const close = text.indexOf("~~", i + 2);
    if (close !== -1)
      return { tag: "s", inner: text.slice(i + 2, close), end: close + 2 };
  }
  if (text[i] === "*" && text[i + 1] !== "*" && text[i + 1] !== " ") {
    const close = text.indexOf("*", i + 1);
    if (close !== -1 && text[close - 1] !== " ")
      return { tag: "em", inner: text.slice(i + 1, close), end: close + 1 };
  }
  return null;
}

// =============================================================================
// Escaping
// =============================================================================

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

function safeHref(href: string): string | null {
  if (SAFE_URL_RE.test(href)) return href;
  return null;
}
